import { useEffect, useRef, useState } from 'react';

import { Delete02Icon, GitBranchIcon, PlusSignIcon, Tick02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { countries } from 'constants/countries';
import { FieldTypes, FieldTypesOptions } from 'constants/field-types-enum';
import { operatorTypes, typesWithLogic } from 'constants/logic';
import { textWithReferences } from 'helpers/form';
import { useLocale } from 'hooks/locale';
import { Controller, useWatch } from 'react-hook-form';

import { Accordion } from 'components/Accordion/Accordion';
import { DatePicker, FormMessage, Select } from 'components/FormComponents';

import editorStyles from './LogicEditor.module.scss';

// ─── Helpers ──────────────────────────────────────────────────────────────────

// A dummy register compatible with Select — does not bind to react-hook-form
const noopRegister = name => ({ name, ref: () => {}, onChange: () => {}, onBlur: () => {} });

// ─── QuestionBadge ────────────────────────────────────────────────────────────

const QuestionBadge = ({ order, className = '' }) => (
  <div
    className={`w-10 h-10 rounded-full bg-neutral-6 flex items-center justify-center shrink-0 ${className}`}>
    <span className="text-neutral-1 text-xl font-regular leading-none">{order}</span>
  </div>
);

// ─── DestinationItem ──────────────────────────────────────────────────────────
// Shared rendering for a question/finish-form option inside a Select

const DestinationItem = ({ field, textParams, compactTrigger = false }) => {
  const typeOption = FieldTypesOptions?.find(opt => opt.key === field.type);
  const previewHtml = textWithReferences(
    field.text?.replace(/<br\s*\/?>/gi, '') ?? '',
    textParams,
    { previewMode: true },
    false,
  );
  return (
    <div
      className={`flex min-w-0 gap-1.5 overflow-hidden flex-group-item ${
        compactTrigger ? 'min-h-0 flex-1 items-center' : 'items-start'
      }`}>
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
          field.isFinishForm ? 'bg-green-2' : 'bg-neutral-6'
        }`}>
        {field.isFinishForm ? (
          <HugeiconsIcon icon={Tick02Icon} size={14} className="text-neutral-1" />
        ) : (
          <span className="text-neutral-1 text-xl font-regular leading-none">{field.order}</span>
        )}
      </div>
      {typeOption?.icon != null && (
        <HugeiconsIcon
          icon={typeOption.icon}
          size="16"
          className={`shrink-0 text-neutral-7 ${compactTrigger ? '' : 'mt-1'}`}
        />
      )}
      {field.isFinishForm ? (
        <span
          className={`text-xl text-neutral-7 ${compactTrigger ? 'min-w-0 flex-1 truncate text-left' : ''}`}>
          {field.text}
        </span>
      ) : compactTrigger ? (
        <div
          className={`${editorStyles.destinationSelectTriggerText} text-xl text-neutral-7`}
          dangerouslySetInnerHTML={{ __html: previewHtml }}
        />
      ) : (
        <span
          className="text-xl text-neutral-7"
          dangerouslySetInnerHTML={{ __html: previewHtml }}
        />
      )}
    </div>
  );
};

// ─── RuleValueField ───────────────────────────────────────────────────────────
// Renders the value input appropriate for the SELECTED field's type

const RuleValueField = ({ fieldName, question, register, control, locale }) => {
  const value = useWatch({ control, name: `${fieldName}.value` });
  const { dateHyphenFormat } = useLocale(locale);

  if (!question) return null;

  let start, end, scale;

  switch (question.type) {
    case FieldTypes.MultipleChoice:
      return (
        <Select name={`${fieldName}.value`} register={register} value={value} useDefault>
          {question?.options?.options?.map(option => (
            <Select.Item key={option.value} value={option.value}>
              <span className="text-xl truncate">{option.value}</span>
            </Select.Item>
          ))}
        </Select>
      );

    case FieldTypes.Date:
      return (
        <Controller
          control={control}
          name={`${fieldName}.value`}
          render={({ field: { onChange, value } }) => (
            <DatePicker
              value={value}
              onChange={onChange}
              format={dateHyphenFormat}
              outputFormat="YYYY-MM-DD"
            />
          )}
        />
      );

    case FieldTypes.YesNo:
      return (
        <Select name={`${fieldName}.value`} register={register} value={value} useDefault>
          <Select.Item value="Yes">Yes</Select.Item>
          <Select.Item value="No">No</Select.Item>
        </Select>
      );

    case FieldTypes.OpinionScale:
      start =
        question.options?.use_custom_steps && question.options?.steps_start != null
          ? parseInt(question.options.steps_start, 10)
          : 1;
      end =
        question.options?.use_custom_steps && question.options?.steps_end != null
          ? parseInt(question.options.steps_end, 10)
          : 10;
      const opinionLen = Number.isFinite(end - start + 1) ? Math.max(1, end - start + 1) : 10;
      scale = Array(opinionLen)
        .fill()
        .map((_, i) => start + i);
      return (
        <Select name={`${fieldName}.value`} register={register} value={value} useDefault>
          {scale.map(n => (
            <Select.Item key={n} value={n}>
              {n}
            </Select.Item>
          ))}
        </Select>
      );

    case FieldTypes.StarRating:
      end =
        question.options?.use_custom_stars && question.options?.stars != null
          ? parseInt(question.options.stars, 10)
          : 5;
      const starLen = Number.isFinite(end) && end >= 2 ? end - 2 : 3;
      scale = Array(starLen)
        .fill()
        .map((_, i) => 1 + i);
      return (
        <Select name={`${fieldName}.value`} register={register} value={value} useDefault>
          {scale.map(n => (
            <Select.Item key={n} value={n}>
              {n}
            </Select.Item>
          ))}
        </Select>
      );

    case FieldTypes.Country:
      return (
        <Select name={`${fieldName}.value`} register={register} value={value} useDefault>
          {countries.map(c => (
            <Select.Item key={c.value} value={c.value}>
              {c.name}
            </Select.Item>
          ))}
        </Select>
      );

    default:
      return null;
  }
};

// ─── RuleCard ─────────────────────────────────────────────────────────────────
// One rule = one logic_jump. The "If [field]" selector lets the user pick which
// question's answer to check. `from` stays fixed to the question card's order.

const RuleCard = ({
  jumpIndex,
  question,
  textParams,
  fields,
  fieldOptions,
  control,
  register,
  setValue,
  onDelete,
  locale,
  logicJumps,
}) => {
  const fieldName = `logic_jumps.${jumpIndex}`;

  const selectedFieldOrder = useWatch({ control, name: `${fieldName}.field` });
  const operatorValue = useWatch({ control, name: `${fieldName}.operator` });
  const toValue = useWatch({ control, name: `${fieldName}.to` });
  const valueWatch = useWatch({ control, name: `${fieldName}.value` });

  const currentFrom = (logicJumps || []).find(({ index }) => index === jumpIndex)?.jump?.from;

  const isDuplicate =
    String(selectedFieldOrder) !== '' &&
    String(operatorValue) !== '' &&
    String(valueWatch ?? '') !== '' &&
    (logicJumps || []).some(({ jump, index: siblingIndex }) => {
      if (!jump || siblingIndex === jumpIndex) return false;
      if (jump.operator === 'finish') return false;
      if (Number(jump.from) !== Number(currentFrom)) return false;
      return (
        String(jump.field) === String(selectedFieldOrder) &&
        String(jump.operator) === String(operatorValue) &&
        String(jump.value) === String(valueWatch)
      );
    });

  // Resolve the full question data for the currently selected field
  const selectedFieldData = (fields || []).find(
    f => Number(f.order) === Number(selectedFieldOrder),
  );

  // Operator options come from the selected field's type, not the card question's type
  const operatorOptions = selectedFieldData
    ? operatorTypes.filter(op => op.allowedTypes.includes(selectedFieldData.type))
    : [];

  // Destination options: questions after this card's question + Finish form
  const toOptions = [
    ...(fields || []).filter(f => f.order > question?.order),
    { order: -1, text: 'Finish form', isFinishForm: true },
  ];

  // Reset operator and value when the user changes which field to check.
  // The ref prevents an unnecessary reset on initial mount.
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    setValue(`${fieldName}.operator`, '');
    setValue(`${fieldName}.value`, '');
  }, [selectedFieldOrder]); // reset when field changes, deps intentionally limited

  return (
    <div className="bg-white border border-neutral-4 rounded-[10px] p-4 flex flex-col gap-4 w-full">
      {/* Condition row: If [field ▼] [operator ▼] [value ▼] [🗑] | + Add condition */}
      <div className="flex items-center gap-2 w-full flex-wrap">
        <div className="flex items-center gap-4 min-w-0 flex-1 basis-0">
          <span className="text-neutral-6 text-xl whitespace-nowrap shrink-0">If</span>
          <div className="min-w-0 flex-1">
            <Select
              name={`${fieldName}.field`}
              register={register}
              value={selectedFieldOrder}
              useDefault
              className="min-w-0 w-full">
              {fieldOptions.map(f => {
                const typeOption = FieldTypesOptions?.find(opt => opt.key === f.type);
                return (
                  <Select.Item key={f.order} value={f.order}>
                    <div className="flex items-start gap-1.5 min-w-0 overflow-hidden flex-group-item">
                      <QuestionBadge order={f.order} />
                      {typeOption?.icon != null && (
                        <HugeiconsIcon
                          icon={typeOption.icon}
                          size="16"
                          className="text-neutral-7 shrink-0 mt-1"
                        />
                      )}
                      <span
                        className="text-xl"
                        dangerouslySetInnerHTML={{
                          __html: textWithReferences(
                            f.text?.replace(/<br\s*\/?>/gi, '') ?? '',
                            textParams,
                            { previewMode: true },
                            false,
                          ),
                        }}
                      />
                    </div>
                  </Select.Item>
                );
              })}
            </Select>
          </div>
        </div>
      </div>

      {/* Jump condition row */}
      <div className="flex items-center gap-4 min-w-0 w-full">
        <span className="text-neutral-6 text-xl whitespace-nowrap">is</span>
        {/* Operator + value: stretch to share space, but allow row to wrap */}
        <div className="flex items-center gap-4 min-w-0 flex-1 basis-0">
          <div className="flex-1 min-w-0">
            <Select
              name={`${fieldName}.operator`}
              register={register}
              value={operatorValue}
              useDefault>
              {operatorOptions.map(op => (
                <Select.Item key={op.value} value={op.value}>
                  <span className="text-xl truncate">{op.label}</span>
                </Select.Item>
              ))}
            </Select>
          </div>
          <div className="flex-1 min-w-0">
            <RuleValueField
              fieldName={fieldName}
              question={selectedFieldData}
              register={register}
              control={control}
              locale={locale}
            />
          </div>
        </div>
      </div>

      {/* Jump destination row — minmax(0,1fr) so Select can shrink below content intrinsic width */}
      <div className="grid w-full min-w-0 grid-cols-[auto_minmax(0,1fr)] items-center gap-4">
        <span className="shrink-0 whitespace-nowrap text-neutral-6 text-xl">then jump to</span>

        <Select
          name={`${fieldName}.to`}
          register={register}
          value={toValue}
          useDefault
          className="min-w-0 w-full"
          renderButtonLabel={val => {
            const f = toOptions.find(x => x.order == val);
            return f ? (
              <DestinationItem field={f} textParams={textParams} compactTrigger />
            ) : undefined;
          }}>
          {toOptions.map(field => (
            <Select.Item key={field.order} value={field.order}>
              <DestinationItem field={field} textParams={textParams} />
            </Select.Item>
          ))}
        </Select>
      </div>

      {/* Duplicate rule warning */}
      {isDuplicate && (
        <FormMessage
          type="Error"
          message="A rule with the same condition already exists. Only the first matching rule will apply."
          closeable={false}
          noMargin
        />
      )}

      {/* Delete Rule */}
      <div className="flex">
        <button
          type="button"
          onClick={onDelete}
          className="flex items-center gap-1 text-red-2 text-lg font-medium whitespace-nowrap hover:text-red-3 transition-colors">
          <HugeiconsIcon icon={Delete02Icon} size={16} />
          Delete Rule
        </button>
      </div>
    </div>
  );
};

// ─── QuestionLogicCard ────────────────────────────────────────────────────────

const QuestionLogicCard = ({
  question,
  jumpsWithIndex,
  fallbackJump,
  textParams,
  fields,
  control,
  register,
  setValue,
  onAddRule,
  onDeleteRule,
  onSetFinishFallback,
  locale,
  isOpen = false,
  onToggleOpen = () => {},
  logicJumps,
}) => {
  const collapsed = !isOpen;

  const nextQuestion = (fields || []).find(f => f.order === question.order + 1);
  const isFinishFallback = !!fallbackJump;
  const ruleCount = jumpsWithIndex.length;

  const fallbackOptions = [
    nextQuestion ? { order: nextQuestion.order, text: nextQuestion.text } : null,
    { order: -1, text: 'Finish form', isFinishForm: true },
  ].filter(Boolean);

  const currentFallbackValue = isFinishFallback ? -1 : (nextQuestion?.order ?? -1);

  const fieldOptions = (fields || []).filter(
    f => typesWithLogic.includes(f.type) && f.order <= question.order,
  );

  const fieldTypeOption = FieldTypesOptions.find(f => f.key === question.type);

  const handleFallbackChange = val => {
    if (Number(val) === -1) {
      if (!isFinishFallback) onSetFinishFallback();
    } else {
      if (isFinishFallback) onDeleteRule(fallbackJump.index);
    }
  };

  return (
    <div className="bg-neutral-1 border border-neutral-4 rounded-3xl p-4 w-full shadow-sm">
      <Accordion
        collapsed={collapsed}
        onToggle={nextCollapsed => onToggleOpen(!nextCollapsed)}
        header={
          <div className="flex items-center gap-3">
            <div
              className={`flex-1 flex  gap-1.5 bg-neutral-2 border border-neutral-4 rounded-2xl px-3 py-2 overflow-hidden ${collapsed ? 'items-center' : 'items-start'}`}>
              <QuestionBadge order={question.order} />
              {fieldTypeOption?.icon != null && (
                <HugeiconsIcon
                  icon={fieldTypeOption.icon}
                  size="16"
                  className={`text-neutral-7 shrink-0 ${collapsed ? '' : 'mt-1'}`}
                />
              )}
              <span
                className={`text-neutral-7 text-xl u-param-preview mt-1 ${collapsed ? 'truncate' : question?.type === FieldTypes.Statement ? 'u-line-clamp-3' : ''}`}
                dangerouslySetInnerHTML={{
                  __html: textWithReferences(
                    question.text?.replace(/<br\s*\/?>/gi, '') ?? '',
                    textParams,
                    { previewMode: true },
                    false,
                  ),
                }}
              />
            </div>
            <div className="relative shrink-0 size-10 flex items-center justify-center">
              <HugeiconsIcon icon={GitBranchIcon} size={20} className="text-neutral-6" />
              {ruleCount > 0 && (
                <span className="absolute -top-1 -right-1 text-neutral-1 text-xs rounded-full w-6 h-6 flex items-center justify-center shrink-0 font-bold bg-primary-4">
                  {ruleCount}
                </span>
              )}
            </div>
          </div>
        }>
        <div id={`question-logic-${question.order}`} className="flex min-w-0 flex-col gap-4">
          {/* Rule cards */}
          {fieldOptions?.length > 0 ? (
            <>
              {jumpsWithIndex.map(({ index }) => (
                <RuleCard
                  key={index}
                  textParams={textParams}
                  jumpIndex={index}
                  question={question}
                  fields={fields}
                  fieldOptions={fieldOptions}
                  control={control}
                  register={register}
                  setValue={setValue}
                  onDelete={() => onDeleteRule(index)}
                  locale={locale}
                  logicJumps={logicJumps}
                />
              ))}

              {/* Add Rule button — hide on last question (no next question to jump to) */}
              {nextQuestion && (
                <button
                  type="button"
                  onClick={onAddRule}
                  className="w-full bg-neutral-2 border border-neutral-4 rounded-xl h-12 flex items-center justify-center gap-1.5 text-neutral-6 text-lg font-medium hover:bg-neutral-3 transition-colors">
                  <HugeiconsIcon icon={PlusSignIcon} size={16} />
                  Add Rule
                </button>
              )}
            </>
          ) : (
            <FormMessage
              type="Info"
              message="This question has no capabilities to set logic jumps"
              closeable={false}
              noMargin
            />
          )}

          {/* ── When no rules match ── */}
          <div className="flex items-center gap-2 px-2">
            <div className="flex-1 h-px bg-neutral-4" />
            <span className="text-color-tertiary text-lg tracking-[0.6px] uppercase whitespace-nowrap">
              {nextQuestion
                ? fieldOptions?.length > 0
                  ? 'When no rules match'
                  : 'Default rule'
                : 'End of form'}
            </span>
            <div className="flex-1 h-px bg-neutral-4" />
          </div>

          {/* Fallback destination */}
          <div className="grid w-full min-w-0 grid-cols-[auto_minmax(0,1fr)] items-center gap-2">
            <span className="shrink-0 whitespace-nowrap text-neutral-6 text-xl">Jumps to</span>

            {isFinishFallback ? (
              <Select
                name={`fallback_${question.order}`}
                register={noopRegister}
                value={currentFallbackValue}
                className="min-w-0 w-full"
                onChange={handleFallbackChange}
                renderButtonLabel={val => {
                  const opt = fallbackOptions.find(o => o.order == val);
                  return opt ? (
                    <DestinationItem field={opt} textParams={textParams} compactTrigger />
                  ) : undefined;
                }}>
                {fallbackOptions.map(opt => (
                  <Select.Item key={opt.order} value={opt.order}>
                    <DestinationItem field={opt} textParams={textParams} />
                  </Select.Item>
                ))}
              </Select>
            ) : (
              <div className="flex items-center gap-2 flex-wrap min-w-0">
                {nextQuestion ? (
                  <div className="flex items-start gap-1.5 min-w-0 flex-1 max-w-full bg-neutral-2 border border-neutral-4 rounded-[10px] px-3 py-2 overflow-hidden">
                    <QuestionBadge order={nextQuestion.order} />
                    <HugeiconsIcon
                      icon={FieldTypesOptions.find(f => f.key === nextQuestion.type)?.icon}
                      size={16}
                      className="text-neutral-7 shrink-0 mt-1"
                    />
                    <span
                      className={`text-neutral-7 text-xl min-w-0 mt-1 ${nextQuestion?.type === FieldTypes.Statement ? 'u-line-clamp-3' : ''}`}
                      dangerouslySetInnerHTML={{
                        __html: textWithReferences(
                          nextQuestion.text?.replace(/<br\s*\/?>/gi, '') ?? '',
                          textParams,
                          { previewMode: true },
                          false,
                        ),
                      }}
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 bg-green-1 border border-green-2 rounded-[10px] px-3 py-2 shrink-0">
                    <div className="w-10 h-10 rounded-full bg-green-2 flex items-center justify-center shrink-0">
                      <HugeiconsIcon icon={Tick02Icon} size={20} className="text-neutral-1" />
                    </div>
                    <span className="text-neutral-7 text-xl">Finish form</span>
                  </div>
                )}

                {nextQuestion && !question.locked && (
                  <button
                    type="button"
                    onClick={onSetFinishFallback}
                    className="text-lg text-green-2 underline hover:text-green-3 transition-colors whitespace-nowrap">
                    Set to Finish form
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </Accordion>
    </div>
  );
};

// ─── LogicEditor ──────────────────────────────────────────────────────────────

const LogicEditor = ({
  textParams,
  control,
  register,
  setValue,
  blockBefore = 0,
  appendLogic,
  removeLogic,
  logicJumpsFields,
  locale = 'en_GB',
}) => {
  const watchFields = useWatch({ control, name: 'fields' }) || [];
  const logicJumpsFromWatch = useWatch({ control, name: 'logic_jumps' }) || [];
  const logicJumps = logicJumpsFields ?? logicJumpsFromWatch;

  // Persist which accordions are open across save/auto-save so they don't collapse
  const [openQuestionOrders, setOpenQuestionOrders] = useState(() => new Set());

  const handleAccordionToggle = (order, open) => {
    setOpenQuestionOrders(prev => {
      const next = new Set(prev);
      if (open) next.add(order);
      else next.delete(order);
      return next;
    });
  };

  const allQuestions = watchFields.filter(field => field.order > blockBefore);

  // Attach each logic jump to its position in the flat array
  const logicJumpsWithIndex = (logicJumps || []).map((jump, index) => ({ jump, index }));

  const handleAddRule = questionOrder => {
    // `field` starts empty — user picks which question to check in the "If" selector
    appendLogic({ from: questionOrder, field: '', operator: '', value: '', to: '' });
  };

  const handleSetFinishFallback = questionOrder => {
    // Store a special jump to mark "when no rules match → finish form"
    // operator:'finish' distinguishes it from regular conditional rules
    appendLogic({
      field: questionOrder,
      from: questionOrder,
      operator: 'finish',
      value: -1,
      to: -1,
    });
  };

  if (allQuestions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-12 text-neutral-5">
        <HugeiconsIcon icon={GitBranchIcon} size={64} />
        <span className="text-3xl text-neutral-7">No questions yet.</span>
        <span className="text-xl text-center max-w-xs text-neutral-6">
          Add questions to the form to configure logic jumps.
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 w-full">
      {allQuestions.map(question => {
        // Regular rules: conditional jumps for this question
        const questionRules = logicJumpsWithIndex.filter(
          ({ jump }) => jump && Number(jump.from) === question.order && jump.operator !== 'finish',
        );

        // Finish fallback: the special 'finish' operator record, if set
        const fallbackJump =
          logicJumpsWithIndex.find(
            ({ jump }) =>
              jump && Number(jump.from) === question.order && jump.operator === 'finish',
          ) ?? null;

        return (
          <QuestionLogicCard
            key={question.id ?? question.key ?? question.order}
            question={question}
            jumpsWithIndex={questionRules}
            fallbackJump={fallbackJump}
            textParams={textParams}
            fields={watchFields}
            control={control}
            register={register}
            setValue={setValue}
            onAddRule={() => handleAddRule(question.order)}
            onDeleteRule={removeLogic}
            onSetFinishFallback={() => handleSetFinishFallback(question.order)}
            locale={locale}
            isOpen={openQuestionOrders.has(question.order)}
            onToggleOpen={open => handleAccordionToggle(question.order, open)}
            logicJumps={logicJumpsWithIndex}
          />
        );
      })}
    </div>
  );
};

export default LogicEditor;
